/**
 * Surface content — 3.4 Design · the solution blueprint and the questions it
 * could not answer.
 *
 * ZERO literals in the component (build brief §0a): every figure, name, id,
 * date, route and sentence this surface renders is declared here or read from
 * `@/demo/data`. Ageing is measured against NOW — never Date.now() — so the
 * deck's `Waiting · 6 days` is derived from the day D-04 was raised.
 *
 * Provenance, per figure:
 *  - CORE (imported, never re-stated): the hero project, the 7-phase
 *    lifecycle, the blueprint drafter, Anouk, the client contact, the module
 *    total (26) and the register's open-item count (14).
 *  - DECK (transcribed here, mockups 3.4): "Solution blueprint · 6 epics"; the
 *    four approaches; the caption "drafted by the blueprint agent from the
 *    register · reviewed by Anouk · 3 sections rewritten"; decision D-04
 *    Overlapping promotions with `Waiting · 6 days`, `+21 h if priority field`
 *    and `Blocks epic 3`; the data-model chain "Company · 6 entities → Store ·
 *    84 → Price list → Promotion rule".
 *  - INVENTED ONCE, HERE: the per-epic module split (which sums to the core's
 *    26), D-05's option pair and its ageing, the two downstream data-model
 *    counts, and the 7-year signature retention.
 *
 * CANONICAL STATE (build brief §0): Van Dijk's phase is BUILD. Design is DONE
 * and its gate is signed, so this surface shows the SIGNED blueprint as the
 * live reference Build works from — with the two decisions the client still
 * owes ageing against Build, not against Design.
 */

import {
  CLIENT_CONTACT,
  NOW,
  PHASES,
  VAN_DIJK,
  agentById,
  heroProject,
  personById,
  thinSpace,
} from '@/demo/data'
import type { PhaseKey } from '@/demo/data'

const HERO = heroProject()
const DRAFTER = agentById('blueprint-drafter')
const ANOUK = personById('anouk-de-vries')
const PIETER = personById('pieter-bakker')

const phase = (key: PhaseKey) => {
  const found = PHASES.find((p) => p.key === key)
  if (!found) throw new Error(`Unknown phase: ${key}`)
  return found
}

const DESIGN = phase('design')
const BUILD = phase('build')

/* ── Dates (relative to NOW — never the wall clock) ─────────────────────── */

const MS_DAY = 86_400_000
const ZONE = 'Europe/Amsterdam'

/** Demo dates are day-precision; anchor them at NOW's time of day. */
const atProjectHour = (isoDay: string): Date => new Date(`${isoDay}T09:00:00+02:00`)

const shortDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: ZONE })
const longDate = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: ZONE,
})

const daysSince = (isoDay: string): number =>
  Math.round((NOW.getTime() - atProjectHour(isoDay).getTime()) / MS_DAY)

const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? '' : 's'}`

/* ── Routes (every affordance on this page lands on one of these) ───────── */

const projectHref = (segment: string): string => `/project/${HERO.id}/${segment}`

export const HREFS = {
  requirements: projectHref('requirements'),
  migration: projectHref('migration'),
  drafter: `/library/agents/${DRAFTER.id}`,
  blueprintConfig: '/library/blueprints',
  reviewer: `/resources/people/${ANOUK.id}`,
  portal: '/portal',
} as const

export const ANCHORS = {
  blueprint: 'solution-blueprint',
  decisions: 'open-decisions',
  dataModel: 'data-model',
  gateRecord: 'gate-record',
} as const

/* ── Chrome ─────────────────────────────────────────────────────────────── */

/**
 * The signature date the portal shows the client for the Design gate.
 * MUST MATCH `DESIGN_SIGNED` in `content/portal.ts` (`2026-07-03`) — it is the
 * same signature, read from two sides of the same project.
 */
const DESIGN_SIGNED_ISO = '2026-07-03'
const DESIGN_SIGNED_LABEL = longDate.format(atProjectHour(DESIGN_SIGNED_ISO))

export const BLUEPRINT_PAGE = {
  moduleLabel: 'PX · Project',
  contextPill: `${DESIGN.name.toUpperCase()} · SIGNED`,
  /** BUILD, not design — the ribbon and the overview describe one world. */
  phase: 'build' as PhaseKey,
  title: 'Solution blueprint & open decisions',
  standfirst:
    `The blueprint the client signed on ${DESIGN_SIGNED_LABEL} is the reference ${BUILD.name} works from. Two ` +
    'questions it could not answer are still with the client, and the older of them is holding an epic that is ' +
    'otherwise ready to build.',
  ribbonNote:
    `${DESIGN.name} closed with the client's signature; the decisions below age against ${BUILD.name}, because ` +
    'that is the phase they are now delaying.',
} as const

/* ── KPI row ────────────────────────────────────────────────────────────── */

export interface BlueprintKpi {
  key: string
  label: string
  figure: string
  qualifier: string
}

/* ── The blueprint ──────────────────────────────────────────────────────── */

/** The four approaches the deck names (3.4) — nothing else may appear. */
export type BlueprintApproach =
  | 'Standard + retail pack'
  | 'Template v4'
  | 'Template v4 + 1 custom'
  | 'Custom, upgrade-safe'

export type EpicStatus = 'Signed' | 'Open decision'

export interface BlueprintEpic {
  key: string
  /** The deck numbers the epics; "Blocks epic 3" names this index. */
  index: number
  name: string
  approach: BlueprintApproach
  /** What that approach costs in code — one line, no adjectives. */
  approachNote: string
  modules: number
  status: EpicStatus
  /** The decision id holding this epic up, when one does. */
  blockedBy: string | null
}

const epic = (e: BlueprintEpic): BlueprintEpic => e

/**
 * Six epics, authored ONCE. The module split is invented here; its sum is
 * asserted against the core's 26 by `MODULES_IN_BLUEPRINT` below, so the table
 * and the build board can never quietly disagree.
 */
export const BLUEPRINT_EPICS: readonly BlueprintEpic[] = [
  epic({
    key: 'pricing',
    index: 1,
    name: 'Pricing',
    approach: 'Template v4',
    approachNote: 'The retail pricing pack, already live at two other retailers.',
    modules: 6,
    status: 'Signed',
    blockedBy: null,
  }),
  epic({
    key: 'store-hierarchy',
    index: 2,
    name: 'Store hierarchy',
    approach: 'Standard + retail pack',
    approachNote: 'Regions, store groups and assortment inheritance need no code.',
    modules: 4,
    status: 'Signed',
    blockedBy: null,
  }),
  epic({
    key: 'promotions',
    index: 3,
    name: 'Promotions',
    approach: 'Template v4 + 1 custom',
    approachNote: 'The pack covers everything except the rule that decides between two promotions.',
    modules: 5,
    status: 'Open decision',
    blockedBy: 'D-04',
  }),
  epic({
    key: 'loyalty',
    index: 4,
    name: 'Loyalty',
    approach: 'Custom, upgrade-safe',
    approachNote: 'Built on the standard loyalty models, no core override.',
    modules: 4,
    status: 'Signed',
    blockedBy: null,
  }),
  epic({
    key: 'inventory',
    index: 5,
    name: 'Inventory',
    approach: 'Standard + retail pack',
    approachNote: 'Inter-store moves and pre-order stock are standard behaviour.',
    modules: 5,
    status: 'Signed',
    blockedBy: null,
  }),
  epic({
    key: 'reporting',
    index: 6,
    name: 'Reporting',
    approach: 'Standard + retail pack',
    approachNote: 'Margin and sell-through views from the pack, no new build.',
    modules: 2,
    status: 'Signed',
    blockedBy: null,
  }),
]

/** Sum of the epic rollups — reads 26, the same total the build board carries. */
export const MODULES_IN_BLUEPRINT = BLUEPRINT_EPICS.reduce((sum, e) => sum + e.modules, 0)

/** The epic the deck's `Blocks epic 3` names — read, never typed twice. */
const blockedEpic = (): BlueprintEpic => {
  const found = BLUEPRINT_EPICS.find((e) => e.blockedBy !== null)
  if (!found) throw new Error('The blueprint declares no blocked epic')
  return found
}

export const BLOCKED_EPIC: BlueprintEpic = blockedEpic()

const SECTIONS_REWRITTEN = 3

export const BLUEPRINT_TABLE = {
  label: 'Solution blueprint',
  heading: `${DESIGN.subLabel} · ${plural(BLUEPRINT_EPICS.length, 'epic')}`,
  columns: {
    epic: 'Epic',
    approach: 'Approach',
    modules: 'Modules',
    status: 'Status',
  },
  modulesHeaderNote: 'count',
  /** Deck-verbatim caption, in the pieces the component links up. */
  caption: {
    draftedBy: 'drafted by the',
    agentLabel: 'blueprint agent',
    agentHref: HREFS.drafter,
    fromLabel: 'from',
    registerLabel: 'the register',
    registerHref: HREFS.requirements,
    reviewedBy: 'reviewed by',
    reviewerName: ANOUK.name,
    reviewerHref: HREFS.reviewer,
    rewritten: `${SECTIONS_REWRITTEN} sections rewritten`,
  },
  agentLine:
    `${DRAFTER.name} · ${DRAFTER.runs} · ${DRAFTER.reviewGate} · ${DRAFTER.acceptancePct}% of its sections ` +
    'accepted as written.',
  blockedNote: (epicName: string, decisionId: string): string =>
    `${epicName} is signed in every section but one: ${decisionId} decides the rule, and the build waits on it.`,
} as const

/* ── Open decisions ─────────────────────────────────────────────────────── */

export type PillTone = 'review' | 'neutral' | 'blocked'

export interface DecisionPill {
  label: string
  tone: PillTone
}

export interface DecisionOption {
  key: string
  label: string
  detail: string
  /** The build cost of choosing this option; null when it costs nothing. */
  effortLabel: string | null
}

export interface OpenDecision {
  id: string
  title: string
  question: string
  options: readonly DecisionOption[]
  /** Who decides — always the client on this surface. */
  owner: string
  ownerName: string
  ownerRole: string
  /** Who on the PX side is waiting for the answer. */
  chasedBy: string
  chasedByRole: string
  askedIso: string
  askedLabel: string
  waitingDays: number
  waitingLabel: string
  pills: readonly DecisionPill[]
  /** Set when an epic cannot be built until this is answered. */
  blocksEpic: BlueprintEpic | null
}

/** Deck 3.4: the priority-field option is the one that costs 21 hours. */
const PRIORITY_FIELD_HOURS = 21
/** Register #61 estimates the franchise price book at 30 h of new build. */
const FRANCHISE_HOURS = 30

const CLIENT_OWNER = `Client · ${HERO.client}`

interface DecisionSeed {
  id: string
  title: string
  question: string
  options: readonly DecisionOption[]
  chasedBy: string
  chasedByRole: string
  askedIso: string
  extraPills: readonly DecisionPill[]
  blocksEpic: BlueprintEpic | null
}

const decision = (seed: DecisionSeed): OpenDecision => {
  const waitingDays = daysSince(seed.askedIso)
  return {
    id: seed.id,
    title: seed.title,
    question: seed.question,
    options: seed.options,
    owner: CLIENT_OWNER,
    ownerName: CLIENT_CONTACT.name,
    ownerRole: `${CLIENT_CONTACT.role} · ${CLIENT_CONTACT.org}`,
    chasedBy: seed.chasedBy,
    chasedByRole: seed.chasedByRole,
    askedIso: seed.askedIso,
    askedLabel: shortDate.format(atProjectHour(seed.askedIso)),
    waitingDays,
    waitingLabel: plural(waitingDays, 'day'),
    pills: [
      { label: `Waiting · ${plural(waitingDays, 'day')}`, tone: 'review' },
      ...seed.extraPills,
    ],
    blocksEpic: seed.blocksEpic,
  }
}

/**
 * D-04 raised 21 Aug and D-05 raised 24 Aug — six and three days before NOW,
 * which is where the deck's `Waiting · 6 days` comes from. Never typed as a
 * number: change NOW or the raised date and the clock moves with it.
 */
export const OPEN_DECISIONS: readonly OpenDecision[] = [
  decision({
    id: 'D-04',
    title: 'Overlapping promotions',
    question: 'Overlapping promotions — which rule wins when two apply to the same article?',
    options: [
      {
        key: 'priority-field',
        label: 'A priority field on the promotion',
        detail:
          'Every promotion carries a priority; the highest one wins, and the store can change it. The most ' +
          'control, and the only option that needs code.',
        effortLabel: `+${PRIORITY_FIELD_HOURS} h`,
      },
      {
        key: 'most-specific-wins',
        label: 'Most-specific-wins',
        detail:
          'The narrowest rule wins — article beats product family beats store group. Standard behaviour, ' +
          'nothing to build, nothing to maintain.',
        effortLabel: null,
      },
      {
        key: 'block-at-entry',
        label: 'Block overlaps at entry',
        detail:
          'A second promotion on the same article for the same period is refused when it is entered. No ' +
          'build, but the buyers lose a way of working they use today.',
        effortLabel: null,
      },
    ],
    chasedBy: ANOUK.name,
    chasedByRole: `${ANOUK.role} · ${ANOUK.office}`,
    askedIso: '2026-08-21',
    extraPills: [
      { label: `+${PRIORITY_FIELD_HOURS} h if priority field`, tone: 'neutral' },
      { label: `Blocks epic ${BLOCKED_EPIC.index}`, tone: 'blocked' },
    ],
    blocksEpic: BLOCKED_EPIC,
  }),
  decision({
    id: 'D-05',
    title: 'Franchise handling',
    question:
      'Franchise handling — do the franchise stores keep their own price book after go-live, or inherit their ' +
      'region’s?',
    options: [
      {
        key: 'own-price-book',
        label: 'Franchise stores keep their own price book',
        detail: 'A price book per franchise, maintained by the franchisee. New build, and a second thing to migrate.',
        effortLabel: `+${FRANCHISE_HOURS} h`,
      },
      {
        key: 'inherit-region',
        label: 'Franchise stores inherit the region',
        detail: 'The franchise reads its region’s price book, like every other store. Nothing to build.',
        effortLabel: null,
      },
    ],
    chasedBy: PIETER.name,
    chasedByRole: `${PIETER.role} · ${PIETER.office}`,
    askedIso: '2026-08-24',
    extraPills: [{ label: `+${FRANCHISE_HOURS} h if franchises price independently`, tone: 'neutral' }],
    blocksEpic: null,
  }),
]

/** "D-04 · D-05" — the KPI qualifier names what it counts. */
export const OPEN_DECISION_REFS = OPEN_DECISIONS.map((d) => d.id).join(' · ')

export const DECISIONS_SECTION = {
  label: 'Open decisions',
  heading: 'Open decisions raised to the client',
  lead:
    'A design question with no number is a conversation. With a number, an owner, a cost and a clock, it is a ' +
    'thing that can be chased — and the client sees the same clock we do.',
  questionLabel: 'Question',
  optionsLabel: 'Options',
  ownerLabel: 'Owner',
  chaserLabel: 'Chased by',
  raisedLabel: 'Raised',
  waitingLabel: 'Waiting',
  waitingUnitNote: 'since it reached the client',
  noCostLabel: 'no build',
  portalLinkLabel: 'visible to the client in the portal →',
  portalHref: HREFS.portal,
  registerLinkLabel: 'the requirement behind it →',
  registerHref: HREFS.requirements,
  blocksLine: (epicName: string, epicIndex: number): string =>
    `Epic ${epicIndex} · ${epicName} cannot be built until this is answered.`,
} as const

/* ── Data model ─────────────────────────────────────────────────────────── */

export interface ModelEntity {
  key: string
  name: string
  count: number
  /** What the count counts — the chip never shows a bare number. */
  unit: string
}

/**
 * The chain the deck draws (Company · 6 entities → Store · 84 → Price list →
 * Promotion rule). Company and Store carry the deck's counts; the two
 * downstream counts are invented ONCE here.
 */
export const DATA_MODEL_CHAIN: readonly ModelEntity[] = [
  { key: 'company', name: 'Company', count: 6, unit: 'legal entities' },
  { key: 'store', name: 'Store', count: 84, unit: 'locations' },
  { key: 'price-list', name: 'Price list', count: 12, unit: 'one per region and channel' },
  { key: 'promotion-rule', name: 'Promotion rule', count: 47, unit: 'live rules today' },
]

export const DATA_MODEL = {
  label: 'Data model',
  heading: 'The spine the blueprint designs against',
  lead:
    'Four entities carry the whole retail model: a rule hangs off a price list, a price list off a store, and ' +
    'a store off a company. It is the chain the promotions decision runs down.',
  caption:
    `Each entity is a migration object with its own field map and load rehearsal — ` +
    `${VAN_DIJK.migration.objects.total} objects in all, of which ${VAN_DIJK.migration.objects.blocked} are ` +
    'blocked on the client.',
  captionLinkLabel: 'Migration objects',
  captionHref: HREFS.migration,
} as const

/* ── Design gate ────────────────────────────────────────────────────────── */

const RETENTION_YEARS = 7
export const BLUEPRINT_VERSION = 'v1'

export const GATE = {
  label: `Gate · ${DESIGN.name}`,
  heading: `${DESIGN.subLabel} — ${DESIGN.gate}`,
  summary: `Solution blueprint signed off — phase gate ${DESIGN.name} → ${BUILD.name} opened`,
  approvedBy: CLIENT_CONTACT.name,
  approvedAt: DESIGN_SIGNED_LABEL,
  consequences: [`${BUILD.name} phase opened`, `Blueprint locked ${BLUEPRINT_VERSION}`] as readonly string[],
  loggedLine: `Gate signatures are retained with the artefact for ${RETENTION_YEARS} years.`,
  overrideHref: `#${ANCHORS.gateRecord}`,
  recordLabel: 'Record',
  recordHeading: 'Signature on file',
  recordColumns: { signer: 'Signer', date: 'Date', artefact: 'Artefact' },
  record: {
    signer: CLIENT_CONTACT.name,
    signerRole: `${CLIENT_CONTACT.role} · ${CLIENT_CONTACT.org}`,
    dateLabel: DESIGN_SIGNED_LABEL,
    artefact: `Solution blueprint ${BLUEPRINT_VERSION} · ${plural(BLUEPRINT_EPICS.length, 'epic')}`,
    artefactNote: `${thinSpace(MODULES_IN_BLUEPRINT)} modules across the six epics`,
  },
  recordNote:
    `The change route after this signature is a change request, not an edit: ${BLUEPRINT_VERSION} is what ` +
    `${BUILD.name} is building against, and the two open decisions are amendments to it.`,
} as const

export const blueprintKpis = (): readonly BlueprintKpi[] => [
  {
    key: 'epics',
    label: 'Epics',
    figure: thinSpace(BLUEPRINT_EPICS.length),
    qualifier: `${thinSpace(MODULES_IN_BLUEPRINT)} modules · ${plural(SECTIONS_REWRITTEN, 'section')} rewritten by a person`,
  },
  {
    key: 'open-decisions',
    label: 'Open decisions',
    figure: thinSpace(OPEN_DECISIONS.length),
    qualifier: `${OPEN_DECISION_REFS} · of the register's ${VAN_DIJK.requirements.open} open items`,
  },
  {
    key: 'gate',
    label: `Gate · ${DESIGN.name}`,
    figure: BLUEPRINT_VERSION,
    qualifier: `signed ${DESIGN_SIGNED_LABEL} by ${CLIENT_CONTACT.name}`,
  },
]

/* ── Density ────────────────────────────────────────────────────────────── */

export const densityNote = (): string =>
  `The blueprint table shows epic-level rollups — ${plural(BLUEPRINT_EPICS.length, 'epic')} over ` +
  `${thinSpace(MODULES_IN_BLUEPRINT)} modules. Section-level detail (the configuration per module, and the ` +
  `field-level design under it) is withheld here: the configuration surface owns it. Decisions show the ` +
  `${OPEN_DECISIONS.length} raised to the client (${OPEN_DECISION_REFS}) of the register's ` +
  `${VAN_DIJK.requirements.open} open items — the other ${VAN_DIJK.requirements.open - OPEN_DECISIONS.length} ` +
  'are open with us, not with them, and sit in the register.'

export const DENSITY_LINKS = {
  configurationLabel: 'Blueprint configuration',
  configurationHref: HREFS.blueprintConfig,
  registerLabel: 'Requirements register',
  registerHref: HREFS.requirements,
} as const
